/**
 * @file launch-check.ts
 * @description Чек-лист первого открытия магазина (phase19.28 этап А; PL8,
 * PL9; SITE_CLOSURE_LAUNCH_CABINET_HINT): что мешает снять паузу «запуск»
 * (договор с платформой, реквизиты продавца, опубликованная политика ПДн —
 * красным, со ссылкой на раздел) и о чём предупредить (жёлтым, публиковать
 * можно). Реестры кодов — новая причина = строка, а не ветка.
 */

import { ApiError } from "../api-client";
import type { SiteLaunchCheck, SiteLaunchMissing, SiteLaunchWarning } from "../api-site-access";

export interface LaunchChecklistItem {
  key: string;
  tone: "error" | "warning";
  text: string;
  /** Раздел кабинета, где это исправить; нет — делает платформа. */
  to?: string;
  linkLabel?: string;
}

interface MissingMeta {
  text: string;
  /** Путь внутри кабинета магазина (после `/shop/<slug>`). */
  path?: string;
  linkLabel?: string;
}

const MISSING_META: Readonly<Record<SiteLaunchMissing, MissingMeta>> = {
  contract: { text: "Нет договора с платформой Commora — его отмечает платформа после подписания" },
  legal_profile: { text: "Не заполнены реквизиты продавца", path: "/settings/legal-profiles", linkLabel: "Реквизиты →" },
  privacy_policy: {
    text: "Не опубликована политика обработки персональных данных",
    path: "/settings/legal-documents",
    linkLabel: "Документы →",
  },
};

/** Предупреждения — по коду; неизвестный код — общий текст с примерами. */
const WARNING_TEXT: Readonly<Record<string, (w: SiteLaunchWarning) => string>> = {
  offer_not_published: () => "Оферта (условия продажи) не опубликована",
  home_not_published: () => "Главная страница не опубликована",
  site_contacts_empty: () => "Контакты сайта не заполнены",
  starter_text_left: (w) => `На странице «${w.pageTitle ?? w.pageSlug ?? "?"}» остался текст шаблона`,
};

/** Цитаты: до 3 и «ещё N». */
function quoted(lines: readonly string[] | undefined): string {
  if (!lines?.length) return "";
  const rest = lines.length - 3;
  return `: ${lines.slice(0, 3).map((l) => `«${l}»`).join(", ")}${rest > 0 ? ` и ещё ${rest}` : ""}`;
}

function warningText(warning: SiteLaunchWarning): string {
  const text = WARNING_TEXT[warning.code];
  const page = warning.pageTitle ?? warning.pageSlug;
  const base = text ? text(warning) : `Проверьте перед открытием${page ? ` (страница «${page}»)` : ""}`;
  return `${base}${quoted(warning.lines)}`;
}

/** Строки чек-листа: сначала блокирующие, затем предупреждения. */
export function launchChecklist(check: SiteLaunchCheck, shopBase: string): LaunchChecklistItem[] {
  const missing = check.missing.map((code): LaunchChecklistItem => {
    const meta = MISSING_META[code] ?? { text: code };
    return {
      key: `missing-${code}`,
      tone: "error",
      text: meta.text,
      ...(meta.path ? { to: `${shopBase}${meta.path}`, linkLabel: meta.linkLabel } : {}),
    };
  });
  const warnings = check.warnings.map((w, i): LaunchChecklistItem => ({ key: `warning-${w.code}-${i}`, tone: "warning", text: warningText(w) }));
  return [...missing, ...warnings];
}

/** Есть ли что показать (иначе сайт открывается сразу). */
export function launchNeedsReview(check: SiteLaunchCheck): boolean {
  return check.blocked || check.missing.length > 0 || check.warnings.length > 0;
}

/** 422 `site_launch_blocked` → чек-лист из `details`; иное — null. */
export function parseLaunchBlocked(error: unknown): SiteLaunchCheck | null {
  if (!(error instanceof ApiError) || error.code !== "site_launch_blocked") return null;
  const details = (error.details ?? {}) as Partial<SiteLaunchCheck>;
  return {
    blocked: true,
    missing: Array.isArray(details.missing) ? details.missing : [],
    warnings: Array.isArray(details.warnings) ? details.warnings : [],
  };
}
